import React, {useEffect, useRef, useState} from "react";
import AITrustSignalLogoBlue from "/AITrustSignalLogoBlue.svg";
import { Modal, VbButton } from "@/components/vb";
import {useNavigate} from "react-router-dom";
import {useReportsStore} from "@/store/store";
import {IProgressReport} from "@/types/report";
import {useAnimatedProgress} from "@/hooks/useAnimatedProgress";
import { useQueryClient } from "@tanstack/react-query";
import { useWorkspace } from "@/context/WorkspaceContext";
import { computeFakeProgressPercent, MIN_GENERATION_DWELL_MS } from "@/lib/progressUtils";

// Shown in the centre slot before the backend has told us which signals this
// run holds — early in a run the report row exists with an empty signal list,
// which used to render the box completely blank. Rotated by the 500ms ticker.
const WARMUP_PHRASES = [
  "Setting up your report...",
  "Checking your website...",
  "Lining up your AI Trust Signals...",
];

const GenerateDialog = ({
  open,
  setOpen,
  reportId,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  reportId?: number | null;
}) => {
  const navigate = useNavigate();
  const [currentProgress, setCurrentProgress] = useState<number>(0);
  const [, setTick] = useState(0); // Ticker to force re-renders for time-based progress
  const [signalNames, setSignalNames] = useState<string[]>([]);
  const [completedSignalsCounter, setCompletedSignalsCounter] = useState(0);
  const [companyId, setCompanyId] = useState<number | null>(null);
  // Selectors, not a bare subscription: this dialog is mounted in the shell, so
  // subscribing to the whole store re-rendered it on every write — including
  // `activeVbCompanyId` and the dialog-request flags — on top of the 5s
  // report-progress tick it actually reads.
  const generatingReports = useReportsStore((s) => s.generatingReports);
  const clearGenerateDialogRequest = useReportsStore((s) => s.clearGenerateDialogRequest);
  const awaitedClaimedAt = useReportsStore((s) => s.awaitedClaimedAt);
  const queryClient = useQueryClient();
  const { activeWorkspace } = useWorkspace();
  // True while we hold a completion on screen to honour the minimum dwell —
  // the render shows the "wrapping up" state during it.
  const [holdingCompletion, setHoldingCompletion] = useState(false);
  const dwellStartedForRef = useRef<number | null>(null);
  const dwellTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const currentReport: IProgressReport | undefined = reportId
    ? generatingReports[reportId]
    : undefined;
  const hasReportData = !!currentReport;

  const hasValidCreatedTs =
    currentReport?.created_ts && currentReport.created_ts.trim() !== "";
  const finalProgress = useAnimatedProgress(
    hasValidCreatedTs ? currentReport.created_ts : "",
    currentProgress,
    open && hasReportData
  );

  useEffect(() => {
    if (open) {
      clearGenerateDialogRequest();
    }
  }, [open, clearGenerateDialogRequest]);

  useEffect(() => {
    if (!open || !reportId || !activeWorkspace?.id) {
      return;
    }

    const refreshReports = () => {
      void queryClient.invalidateQueries({
        queryKey: ["reports", activeWorkspace.id],
        exact: true,
      });
    };

    refreshReports();
    const interval = setInterval(refreshReports, 3000);

    return () => {
      clearInterval(interval);
    };
  }, [activeWorkspace?.id, open, queryClient, reportId]);

  useEffect(() => {
    if (open) {
      // An errored run is handled in the render below: the dialog stays up and
      // says so. It used to navigate to `/` and close here — a silent failure
      // that dropped the user on the dashboard with no idea the run died.
      if (!reportId) {
        navigate(`/`);
        setOpen(false);
        return;
      } else if (reportId && currentReport?.status === "completed") {
        // Run the bar to 100% and get out of the way. The navigation itself is
        // NOT done here any more: it was gated on this dialog being open, so
        // dismissing it (which the copy below invites) meant the user was never
        // taken to their finished report. `useAwaitedReport` in the app shell
        // owns it now, on the same beat, whether or not this is mounted.
        //
        // Minimum dwell: a month that already has a completed report resolves
        // in ~2s (the backend deduplicates), and an instant 100%-and-navigate
        // reads as fake. Hold the completion and walk the bar up instead;
        // `useAwaitedReport` waits out the same floor before navigating.
        if (dwellStartedForRef.current === reportId) return;
        dwellStartedForRef.current = reportId;

        const sinceClaim = awaitedClaimedAt != null ? Date.now() - awaitedClaimedAt : Infinity;
        const remaining = Math.max(0, MIN_GENERATION_DWELL_MS - sinceClaim);

        if (remaining <= 0) {
          setCurrentProgress(100);
          const timer = setTimeout(() => setOpen(false), 500);
          dwellTimersRef.current.push(timer);
          return;
        }

        setHoldingCompletion(true);
        const stage = (at: number, fn: () => void) =>
          dwellTimersRef.current.push(setTimeout(fn, at));
        stage(0, () => setCurrentProgress(45));
        stage(remaining * 0.35, () => setCurrentProgress(70));
        stage(remaining * 0.7, () => setCurrentProgress(88));
        stage(remaining * 0.9, () => setCurrentProgress(97));
        stage(remaining, () => {
          setHoldingCompletion(false);
          setCurrentProgress(100);
        });
        stage(remaining + 500, () => setOpen(false));
      }
    }
  }, [
    currentReport?.status,
    reportId,
    awaitedClaimedAt,
    navigate,
    setOpen,
    open,
  ]);

  // The dwell timers survive effect re-runs on purpose (the once-guard above
  // stops re-staging); they only die with the dialog.
  useEffect(() => {
    if (open) return;
    dwellTimersRef.current.forEach(clearTimeout);
    dwellTimersRef.current = [];
    dwellStartedForRef.current = null;
    setHoldingCompletion(false);
  }, [open]);
  useEffect(() => () => dwellTimersRef.current.forEach(clearTimeout), []);

  // Ticker to update time-based progress every 500ms
  useEffect(() => {
    if (
      !open ||
      !currentReport ||
      currentReport.status === "completed" ||
      currentReport.status === "error"
    ) {
      return;
    }

    const interval = setInterval(() => {
      setTick(t => t + 1);
    }, 500);

    return () => clearInterval(interval);
  }, [open, currentReport, currentReport?.status]);

  useEffect(() => {
    if (!open) {
      setCurrentProgress(0);
      setSignalNames([]);
      setCompletedSignalsCounter(0);
      setCompanyId(null);
      return;
    }

    if (!currentReport) {
      setCurrentProgress(0);
      setSignalNames([]);
      setCompletedSignalsCounter(0);
      return;
    }

    if (currentReport.signals) {
      let completedSignals = 0;
      const names: string[] = [];

      if (companyId !== currentReport.company_id) {
        setCompanyId(currentReport.company_id);
      }

      currentReport.signals.forEach((signal) => {
        names.push(signal.name);
        if (signal.status === "completed") {
          completedSignals++;
        }
      });

      setSignalNames(names);
      setCompletedSignalsCounter(completedSignals);
      // Use fake progress calculation with time-based progression. A completed
      // report is deliberately NOT written here (it computes to 100): the
      // completion effect above owns the endgame, including the staged walk-up
      // that paces a near-instant resolution.
      if (currentReport.status !== "completed") {
        setCurrentProgress(computeFakeProgressPercent(currentReport));
      }
    }
  }, [companyId, currentReport, open]); // Ticker is in separate effect above

  const previousSignal =
    completedSignalsCounter > 0 && completedSignalsCounter - 1 < signalNames.length
      ? signalNames[completedSignalsCounter - 1]
      : null;
  const nextSignal =
    completedSignalsCounter + 1 < signalNames.length
      ? signalNames[completedSignalsCounter + 1]
      : null;

  // Every signal is scored but the report hasn't flipped to completed — the
  // ~60s summary/finalize tail. The dashboard behind this dialog can already be
  // fully rendered by now, so a bar stuck at ~93% "Finalizing" reads as a
  // contradiction (or a hang). Say what is actually happening instead. Display
  // only: closing stays keyed on `status === "completed"` alone, never on a
  // percentage.
  const allSignalsScored =
    signalNames.length > 0 &&
    completedSignalsCounter >= signalNames.length &&
    currentReport?.status !== "completed";
  // While holding a completion for the minimum dwell, the staged progress in
  // `currentProgress` drives the bar (no 97 floor — it walks up on its own).
  const wrappingUp = allSignalsScored || holdingCompletion;
  const displayProgress = allSignalsScored ? Math.max(finalProgress, 97) : finalProgress;

  const failed = currentReport?.status === "error";

  if (failed) {
    // Say so, in place. Closing leaves the user exactly where they were — the
    // dashboard behind this shows the generate prompt again for a failed
    // current month, which is the retry path. (Update-framing holds here too:
    // nothing about any previous report.)
    return (
      <Modal
        open={open}
        onOpenChange={setOpen}
        srTitle="Report generation failed"
        widthClassName="max-w-[560px]"
        bare
      >
        <div className="flex flex-col items-center gap-5 text-center">
          <div
            className="h-[64px] w-[180px] bg-contain bg-center bg-no-repeat"
            style={{ backgroundImage: `url('${AITrustSignalLogoBlue}')` }}
            role="img"
            aria-label="AI Trust Signal"
          />
          <h2 className="text-[19px] font-bold leading-snug tracking-[-0.012em] text-vb-text">
            We couldn't finish your report
          </h2>
          <p className="text-[13.5px] leading-relaxed text-vb-text-3">
            Something went wrong while checking your AI Trust Signals. Please try
            running the report again in a few minutes. If it keeps happening,
            contact support and we'll take a look.
          </p>
          <VbButton onClick={() => setOpen(false)}>Close</VbButton>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onOpenChange={setOpen}
      srTitle="Generate Report"
      widthClassName="max-w-[560px]"
      bare
    >
      <div className="flex flex-col items-center gap-5 text-center">
        <div
          className="h-[64px] w-[180px] bg-contain bg-center bg-no-repeat"
          style={{ backgroundImage: `url('${AITrustSignalLogoBlue}')` }}
          role="img"
          aria-label="AI Trust Signal"
        />

        <h2 className="text-[19px] font-bold leading-snug tracking-[-0.012em] text-vb-text">
          Our reports take time to run
        </h2>

        <div className="flex w-full flex-col gap-2">
          <div className="h-2 w-full overflow-hidden rounded-vb-pill bg-vb-bg-subtle">
            <div
              className="h-full rounded-vb-pill bg-vb-brand transition-all duration-300 ease-out"
              style={{ width: `${Math.max(0, Math.min(100, displayProgress))}%` }}
            />
          </div>
          <div className="flex justify-between text-[12px] font-medium text-vb-text-3">
            <span>{wrappingUp ? "Wrapping up..." : "Generating report..."}</span>
            <span>{Math.round(displayProgress)}%</span>
          </div>
        </div>

        <div className="flex w-full flex-col items-center gap-1 rounded-vb-md border border-vb-line bg-vb-bg-subtle px-4 py-3">
          <div className="h-4 text-[12px] font-medium text-vb-text-3">{previousSignal}</div>
          <div className="text-[13.5px] font-bold text-vb-text">
            {!currentReport
              ? "Preparing report status..."
              : wrappingUp
                ? "All signals scored. Finishing your summary..."
                : currentReport.status === "in_progress"
                  ? signalNames[completedSignalsCounter] ??
                    WARMUP_PHRASES[
                      Math.floor(Date.now() / 2500) % WARMUP_PHRASES.length
                    ]
                  : "Finalizing your report"}
          </div>
          <div className="h-4 text-[12px] font-medium text-vb-text-3">{nextSignal}</div>
        </div>

        <p className="text-[12px] text-vb-text-3">
          You can safely close this window - your report will keep generating.
        </p>
      </div>
    </Modal>
  );
};

export default GenerateDialog;
